import { useEffect, useState } from "react";
import { MdToday } from "react-icons/md";
import { Button, Tooltip } from "@/components/ui";
import Input from "@/components/ui/input";
import { FilterProps } from "./types";
const DateSelect: React.FC<FilterProps> = ({
  onChange
}) => {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  useEffect(() => {
    handleDateChange();
  }, [month, day, year]);

  // Year must be either empty (search all years) or exactly 4 digits for a valid ISO date
  const isYearValid = year === "" || year.length === 4;
  const isYearIncomplete = year && year.length < 4;

  // Check if the date combination is valid (e.g., Feb 31 is invalid)
  const isDateValid = (m: string, d: string, y: string): boolean => {
    if (!m || !d) return false;
    const monthNum = parseInt(m);
    const dayNum = parseInt(d);
    // Use a non-leap year (2001) when year is empty to prevent Feb 29 selection
    // since Feb 29 without a year would match non-existent dates in non-leap years
    const yearNum = y ? parseInt(y) : 2001;
    const date = new Date(yearNum, monthNum - 1, dayNum);
    // Check if the date components match what we passed in
    return date.getFullYear() === yearNum && date.getMonth() === monthNum - 1 && date.getDate() === dayNum;
  };
  const handleDateChange = () => {
    if (month && day && isYearValid && isDateValid(month, day, year)) {
      // If year is provided (and valid per isYearValid), use it; otherwise, search across all years
      if (year) {
        const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        onChange([formattedDate, formattedDate]);
      } else {
        // For month/day only filtering, we'll use a special format that the backend can handle
        const monthDayOnly = `--${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        onChange([monthDayOnly, monthDayOnly]);
      }
    } else {
      // If partial date, invalid year, or invalid date combination, don't filter
      onChange(undefined);
    }
  };
  const validateInput = (value: string, max: number, setter: (value: string) => void) => {
    const numValue = value.replace(/\D/g, "");
    if (numValue === "" || parseInt(numValue) <= max && parseInt(numValue) > 0) {
      setter(numValue);
    }
  };
  const setToToday = () => {
    const today = new Date();
    setMonth((today.getMonth() + 1).toString());
    setDay(today.getDate().toString());
    // Don't set year - let user decide if they want to filter by year or not
  };
  return <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Input type="text" value={month} onChange={e => validateInput(e.target.value, 12, setMonth)} placeholder="MM" maxLength={2} className="w-12 text-center" enableStepper={false} />
        <Input type="text" value={day} onChange={e_0 => validateInput(e_0.target.value, 31, setDay)} placeholder="DD" maxLength={2} className="w-12 text-center" enableStepper={false} />
        <Input type="text" value={year} onChange={e_1 => {
        const value_0 = e_1.target.value.replace(/\D/g, "");
        // Reject all-zeros input (e.g., "0000") to avoid confusing UX
        if (/^0+$/.test(value_0) && value_0.length > 0) {
          return;
        }
        const stripped = value_0.replace(/^0+/, "") || "";
        // Only validate against max year when we have 4 digits to allow
        // typing years like "2026" digit-by-digit without rejection
        const maxYear = stripped.length === 4 ? new Date().getFullYear() : 9999;
        validateInput(stripped, maxYear, setYear);
      }} placeholder="YYYY" maxLength={4} className="w-20 text-center" enableStepper={false} hasError={!!isYearIncomplete} errorMessage="Please enter a 4-digit year" />
      </div>
      <p className="text-foreground-secondary">
        Leave year blank to search all years
      </p>
      {month && day && isYearValid && !isDateValid(month, day, year) && <p className="text-status-error-text text-body">Invalid date</p>}

      <Tooltip content="Set to today's month and day (year will be left blank)" onClick={e_2 => e_2.stopPropagation()}>
        <Button type="button" variant="secondary" onClick={setToToday} className="w-full gap-2 px-2 py-1.5">
          <MdToday className="h-4 w-4" />
          Today&apos;s Month/Day
        </Button>
      </Tooltip>
    </div>;
};
export default DateSelect;